import React, { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Box, IconButton, SvgIcon } from "@mui/material"
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid";
import { formatDate, getHour } from "../../utils/time";
import { CustomDataGrid } from "../CustomDataGrid";
import { Refund } from "../../types/Refund";
import Icon from "@mdi/react";
import { mdiSquareEditOutline } from "@mdi/js";
import { IProps } from "./IProps";

const defaultColumnOptions = {
    resizable: false,
    flex: 1,
    hideSortIcons: false,
    disableColumnMenu: false,
}

const columns: GridColDef[] = [
    {
        field: "refundId",
        headerName: "ID",
        align: "left",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "dateRequested",
        headerName: "Date Requested",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {formatDate(params.value)}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "callDateTime",
        headerName: "Lead Date",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {formatDate(params.value)}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "refundDescr",
        headerName: "Refund Reason",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {params.value === null ? "n/a" : params.value}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "price",
        headerName: "Lead Price",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "dealer",
        headerName: "Dealer",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "statusDescr",
        headerName: "Current Status",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "callerLastName",
        headerName: "Lastname",
        headerAlign: "center",
        align: "center",
        renderHeader: (params: GridColumnHeaderParams) => {
            return (
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {params.colDef.headerName!}
                </Typography>
            );
        },
        renderCell: (params: GridRenderCellParams) => {

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <Typography sx={{ fontWeight: 400, fontSize: 14 }} >
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        ...defaultColumnOptions
    },
    {
        field: "edit",
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
            const navigate = useNavigate();

            return (
                <Box sx={{ mt: 1, mb: 1 }} >
                    <IconButton color="primary" sx={{ border: "2px solid", borderRadius: 1 }} onClick={() => navigate(`/refund-edit/${params.row.refundId}`)}>
                        <SvgIcon>
                            <Icon path={mdiSquareEditOutline}  />
                        </SvgIcon>
                    </IconButton>
                </Box>
            )
        },
        ...defaultColumnOptions
    }
]

export const RefundGrid: FunctionComponent<IProps> = (props): JSX.Element => {
    return (
        <CustomDataGrid
            columns={columns}
            rows={props.rows}
            page={props.page}
            pageSize={props.pageSize}
            rowsPerPageOptions={[5, 10, 15, 25]}
            onPageChange={props.onPageChange}
            onPageSizeChange={props.onPageSizeChange}
            pagination
            paginationMode="server"
            rowCount={props.total}
            sortingOrder={["asc", "desc"]}
        />
    )
}